import { useState, useEffect } from "react";
import { XIcon, UploadIcon, CheckIcon, AlertCircleIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { AcademicianStartupButton } from "@/components/academician/academicianstartup-button";
import { cn } from "@/lib/utils";
import type { Video } from "@/components/academician/video-card";

export interface VideoFormData {
  id?: string;
  title: string;
  description: string;
  videoUrl: string;
  thumbnail: string;
  duration: string;
  status: "published" | "draft";
  transcript?: string;
}

interface VideoFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  video?: VideoFormData;
  onSave?: (data: VideoFormData) => void;
  onCancel?: () => void;
}

// Helper function to validate YouTube/Vimeo URLs
function validateVideoUrl(url: string): {
  valid: boolean;
  platform?: "youtube" | "vimeo";
  videoId?: string;
} {
  // YouTube patterns
  const youtubePatterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
  ];

  // Vimeo pattern
  const vimeoPattern = /vimeo\.com\/(\d+)/;

  for (const pattern of youtubePatterns) {
    const match = url.match(pattern);
    if (match) {
      return { valid: true, platform: "youtube", videoId: match[1] };
    }
  }

  const vimeoMatch = url.match(vimeoPattern);
  if (vimeoMatch) {
    return { valid: true, platform: "vimeo", videoId: vimeoMatch[1] };
  }

  return { valid: false };
}

// Helper function to get thumbnail URL
function getThumbnailUrl(
  platform: "youtube" | "vimeo",
  videoId: string
): string {
  if (platform === "youtube") {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  } else {
    // For Vimeo, we'd need to make an API call, so we'll use a placeholder
    return `https://vumbnail.com/${videoId}.jpg`;
  }
}

export function VideoFormModal({
  open,
  onOpenChange,
  video,
  onSave,
  onCancel,
}: VideoFormModalProps) {
  const [formData, setFormData] = useState<VideoFormData>({
    title: "",
    description: "",
    videoUrl: "",
    thumbnail: "",
    duration: "",
    status: "draft",
    transcript: "",
  });

  const [urlValidation, setUrlValidation] = useState<{
    valid: boolean;
    message?: string;
  }>({ valid: true });
  const [isLoading, setIsLoading] = useState(false);
  const [transcriptFile, setTranscriptFile] = useState<File | null>(null);

  // Initialize form with video data if editing
  useEffect(() => {
    if (video) {
      setFormData(video);
    } else {
      setFormData({
        title: "",
        description: "",
        videoUrl: "",
        thumbnail: "",
        duration: "",
        status: "draft",
        transcript: "",
      });
    }
    setUrlValidation({ valid: true });
    setTranscriptFile(null);
  }, [video, open]);

  // Validate and auto-fetch thumbnail when URL changes
  useEffect(() => {
    if (!formData.videoUrl) {
      setUrlValidation({ valid: true });
      return;
    }

    const validation = validateVideoUrl(formData.videoUrl);

    if (validation.valid && validation.platform && validation.videoId) {
      setUrlValidation({
        valid: true,
        message: `Valid ${validation.platform} URL`,
      });

      // Auto-fetch thumbnail
      const thumbnailUrl = getThumbnailUrl(
        validation.platform,
        validation.videoId
      );
      setFormData((prev) => ({ ...prev, thumbnail: thumbnailUrl }));
    } else {
      setUrlValidation({
        valid: false,
        message: "Please enter a valid YouTube or Vimeo URL",
      });
      setFormData((prev) => ({ ...prev, thumbnail: "" }));
    }
  }, [formData.videoUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!urlValidation.valid) {
      return;
    }

    setIsLoading(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    onSave?.(formData);
    setIsLoading(false);
    onOpenChange(false);
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const handleTranscriptUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setTranscriptFile(file);
      // In a real app, you'd read the file content here
      setFormData((prev) => ({ ...prev, transcript: file.name }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {video ? "Edit Video" : "Upload Video"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, title: e.target.value }))
              }
              placeholder="Enter video title"
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Enter video description"
              rows={4}
            />
          </div>

          {/* Video URL */}
          <div className="space-y-2">
            <Label htmlFor="videoUrl">
              Video URL (YouTube or Vimeo){" "}
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id="videoUrl"
              value={formData.videoUrl}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, videoUrl: e.target.value }))
              }
              placeholder="https://www.youtube.com/watch?v=..."
              required
              className={cn(
                !urlValidation.valid &&
                  formData.videoUrl &&
                  "border-destructive"
              )}
            />

            {formData.videoUrl && (
              <div
                className={cn(
                  "flex items-center gap-2 text-sm",
                  urlValidation.valid
                    ? "text-green-600 dark:text-green-400"
                    : "text-destructive"
                )}
              >
                {urlValidation.valid ? (
                  <CheckIcon className="h-4 w-4" />
                ) : (
                  <AlertCircleIcon className="h-4 w-4" />
                )}
                <span>{urlValidation.message}</span>
              </div>
            )}
          </div>

          {/* Thumbnail Preview */}
          {formData.thumbnail && (
            <div className="space-y-2">
              <Label>Thumbnail Preview</Label>
              <div className="relative aspect-video bg-muted rounded-lg overflow-hidden border border-border">
                <img
                  src={formData.thumbnail}
                  alt="Video thumbnail"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback if thumbnail fails to load
                    e.currentTarget.src =
                      "https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800&h=450&fit=crop";
                  }}
                />
              </div>
            </div>
          )}

          {/* Duration */}
          <div className="space-y-2">
            <Label htmlFor="duration">
              Duration <span className="text-destructive">*</span>
            </Label>
            <Input
              id="duration"
              value={formData.duration}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, duration: e.target.value }))
              }
              placeholder="e.g., 12:34 or 1:23:45"
              required
            />

            <p className="text-xs text-muted-foreground">
              Format: MM:SS or HH:MM:SS
            </p>
          </div>

          {/* Status Toggle */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="space-y-0.5">
              <Label htmlFor="status" className="text-base">
                Publish Status
              </Label>
              <p className="text-sm text-muted-foreground">
                {formData.status === "published"
                  ? "Video is visible to all users"
                  : "Video is saved as draft"}
              </p>
            </div>
            <Switch
              id="status"
              checked={formData.status === "published"}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({
                  ...prev,
                  status: checked ? "published" : "draft",
                }))
              }
            />
          </div>

          {/* Transcript Upload */}
          <div className="space-y-2">
            <Label htmlFor="transcript">Transcript (Optional)</Label>
            <div className="flex items-center gap-3">
              <Input
                id="transcript"
                type="file"
                accept=".txt,.srt,.vtt"
                onChange={handleTranscriptUpload}
                className="flex-1"
              />

              {transcriptFile && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckIcon className="h-4 w-4 text-green-600" />
                  <span>{transcriptFile.name}</span>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Upload a transcript file (.txt, .srt, or .vtt)
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <AcademicianStartupButton
              type="button"
              variant="ghost"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </AcademicianStartupButton>
            <AcademicianStartupButton
              type="submit"
              variant="primary"
              loading={isLoading}
              disabled={
                !urlValidation.valid ||
                !formData.title ||
                !formData.videoUrl ||
                !formData.duration
              }
            >
              {video ? "Save Changes" : "Upload Video"}
            </AcademicianStartupButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
